import { cookieStorage } from '@muse-web/storages'
import { webLogger } from '@muse-web/logger'

import { sendEventsBatch } from '../services/sendEventsBatch'
import { EXPERIMENT_MEMBER_COOKIE_NAME } from '../constants/cookieKeys'
import { type TAnalyticEvent } from '../types/analyticEventTypes'
import { type TEventForm } from '../types/eventFormTypes'

import { formatSentEventError, type TSentEventError } from './formatSentEventError'
import { getReleaseParams } from './analyticsRelease'
import { getAnalyticsTrigger } from './analyticsTrigger'
import { getClientReferrer } from './getClientReferrer'
import { getScreenForPath } from './currentScreen'
import { withMtmEntities } from './mtmEntities'
import { isCrawler } from './isCrawler'
import { touchSession } from './touchSession'

const FLUSH_DELAY = 3000
const isCrawlerClient = typeof navigator !== 'undefined' && isCrawler(navigator.userAgent)

export type TEventDeliveryResult = {
    delivered: boolean
    error?: TSentEventError
    failure?: unknown
}

const eventsBatch: TEventForm[] = []
let flushTimer: ReturnType<typeof setTimeout> | null = null

const readSessionIds = (sessionId: string) => ({
    member_experiment_id: Number(cookieStorage.get(EXPERIMENT_MEMBER_COOKIE_NAME)),
    session_id: Number(sessionId),
})

// audio-web reports nothing from here in production (only a dev-mode toast): the caller that awaited
// the delivery is the one that logs. Reporting here as well would send two Sentry lines for one
// failed batch, since `Logger.error` in `@core/logger` is this same webLogger.
const logTransportError = (error: unknown) => {
    if (__DEV__) {
        webLogger.error(error instanceof Error ? error : String(error))
    }
}

const flush = (): Promise<TEventDeliveryResult> => {
    if (flushTimer !== null) {
        clearTimeout(flushTimer)
        flushTimer = null
    }

    if (eventsBatch.length === 0) {
        return Promise.resolve({ delivered: true })
    }

    const eventsToSend = eventsBatch.splice(0, eventsBatch.length)

    return sendEventsBatch(eventsToSend)
        .then((): TEventDeliveryResult => ({ delivered: true }))
        .catch((error: unknown): TEventDeliveryResult => {
            logTransportError(error)

            return { delivered: false, error: formatSentEventError(error), failure: error }
        })
}

const scheduleFlush = () => {
    if (flushTimer !== null || eventsBatch.length === 0) {
        return
    }

    flushTimer = setTimeout(() => {
        flushTimer = null
        flush()
    }, FLUSH_DELAY)
}

const getReferrerDomain = (): string => {
    try {
        return new URL(getClientReferrer()).hostname
    } catch {
        return 'direct'
    }
}

const getQuery = (): string | undefined => {
    try {
        return withMtmEntities(window.location.search)
    } catch (error) {
        webLogger.error(error instanceof Error ? error : String(error))
        return undefined
    }
}

const isStringList = (value: unknown): value is string[] =>
    Array.isArray(value) && value.every(item => typeof item === 'string')

const toStringList = (value: unknown): string[] =>
    Array.isArray(value)
        ? Array.from(value, (item: unknown) =>
              item === undefined || item === null ? '' : String(item),
          )
        : []

const toNumberList = (value: unknown): number[] =>
    Array.isArray(value)
        ? Array.from(value, (item: unknown) =>
              typeof item === 'number' && !Number.isNaN(item) ? item : 0,
          )
        : []

const fitTo = <T>(values: T[], length: number, filler: T): T[] => [
    ...values.slice(0, length),
    ...Array.from({ length: Math.max(length - values.length, 0) }, () => filler),
]

const withReleaseParams = (payload: Record<string, unknown>): Record<string, unknown> => {
    const release = getReleaseParams()

    if (release === null) {
        return payload
    }

    const ownKeys = isStringList(payload.params__key) ? payload.params__key : []
    const ownValues = toNumberList(payload.params__value)
    const ownStrValues = toStringList(payload.params__str_value)

    return {
        ...payload,
        params__key: [...ownKeys, ...release.params__key],
        params__value: [...fitTo(ownValues, ownKeys.length, 0), ...release.params__value],
        params__str_value: [
            ...fitTo(ownStrValues, ownKeys.length, ''),
            ...release.params__str_value,
        ],
    }
}

// Enrich the event with the shared context params (screen from the router, ids from
// cookies, referrer/query) into a plain TEventForm — the shape the transport sends.
// No mutation of the caller's event.
const enrichEvent = (event: TAnalyticEvent): TEventForm => {
    if (typeof window === 'undefined') {
        return { name: event.name, payload: withReleaseParams(event.payload) }
    }

    return {
        name: event.name,
        payload: withReleaseParams({
            ...event.payload,
            screen: getScreenForPath(window.location.pathname),
            url: window.location.pathname,
            referrer: getReferrerDomain(),
            ...readSessionIds(touchSession()),
            trigger: getAnalyticsTrigger(),
            query: getQuery(),
        }),
    }
}

export const sendEvent = async (
    event: TAnalyticEvent,
    isImmediate = false,
): Promise<TEventDeliveryResult> => {
    if (isCrawlerClient) {
        return { delivered: true }
    }

    const form = enrichEvent(event)
    eventsBatch.push(form)

    if (__DEV__) {
        webLogger.log(['analytics event:', form.name, form.payload])
    }

    if (isImmediate) {
        return flush()
    }

    scheduleFlush()

    return { delivered: true }
}

if (typeof window !== 'undefined') {
    // `pagehide` covers navigation and bfcache; `visibilitychange` covers Android backgrounding the
    // tab, where the page can be frozen and never fire `pagehide` at all. The transport sets
    // `keepalive`, so a request started here outlives the page — the flush only has to reach the
    // network call.
    window.addEventListener('pagehide', () => {
        flush()
    })

    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
            flush()
        }
    })
}
